<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesCommissionParticipantPayment from './SalesCommissionParticipantPayment.vue';
import {
  formatCnpj,
  formatCpf,
  formatPixDocument,
  isValidCnpj,
  isValidCpf,
  isValidPixDocument,
} from '../composables/commissionPaymentValidation';

const props = defineProps({
  banks: { type: Array, default: () => [] },
  errors: { type: Object, default: () => ({}) },
  participant: { type: Object, default: null },
  pixKeys: { type: Array, default: () => [] },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const draft = ref({});
const optionLabel = option => option.label || option.name || option.nome;
const documentField = computed(() =>
  draft.value.person_type === 'PJ' ? 'cnpj' : 'cpf'
);
const documentLabel = computed(() =>
  documentField.value === 'cnpj'
    ? t('SALES.COMMISSION.FIELDS.CNPJ')
    : t('SALES.COMMISSION.FIELDS.CPF')
);
const documentInvalid = computed(() =>
  documentField.value === 'cnpj'
    ? t('SALES.COMMISSION.ERRORS.CNPJ_INVALID')
    : t('SALES.COMMISSION.ERRORS.CPF_INVALID')
);
const documentError = computed(() => {
  const value = draft.value[documentField.value];
  if (!value) return props.errors.document || '';
  const valid =
    documentField.value === 'cnpj' ? isValidCnpj(value) : isValidCpf(value);
  return valid ? '' : documentInvalid.value;
});
const pixError = computed(() => {
  if (draft.value.payment_type !== 'pix') return '';
  const isDocument = Number(draft.value.pix_key_type) === 1;
  if (!isDocument || !draft.value.pix_key) return '';
  return isValidPixDocument(draft.value.pix_key)
    ? ''
    : t('SALES.COMMISSION.ERRORS.PIX_DOCUMENT_INVALID');
});
const valid = computed(() => !documentError.value && !pixError.value);

watch(
  () => [props.show, props.participant],
  ([show]) => {
    if (show) draft.value = { ...props.participant };
  },
  { immediate: true }
);

const update = (field, value) => {
  if (field === 'bank_id') {
    const bank = props.banks.find(
      option => String(option.id) === String(value)
    );
    draft.value = {
      ...draft.value,
      bank_id: value,
      bank_name: bank ? optionLabel(bank) : '',
    };
    return;
  }

  let normalized = value;
  if (field === 'cpf') normalized = formatCpf(value);
  if (field === 'cnpj') normalized = formatCnpj(value);
  if (field === 'pix_key' && Number(draft.value.pix_key_type) === 1) {
    normalized = formatPixDocument(value);
  }
  draft.value = { ...draft.value, [field]: normalized };
};
const save = () => {
  if (valid.value) emit('save', { ...draft.value });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="t('SALES.COMMISSION.PARTICIPANTS.PAYMENT_TITLE')"
    max-width-class="max-w-[920px]"
    @close="emit('close')"
  >
    <h3 class="mb-4 text-base font-semibold text-lais-text">
      {{ draft.name }}
    </h3>
    <div class="mb-4 flex flex-wrap gap-5">
      <label class="flex items-center gap-2 text-sm text-lais-text">
        <input
          type="radio"
          value="PF"
          :checked="draft.person_type !== 'PJ'"
          class="accent-lais-primary"
          @change="update('person_type', 'PF')"
        />
        {{ t('SALES.COMMISSION.PERSON_TYPES.INDIVIDUAL') }}
      </label>
      <label class="flex items-center gap-2 text-sm text-lais-text">
        <input
          type="radio"
          value="PJ"
          :checked="draft.person_type === 'PJ'"
          class="accent-lais-primary"
          @change="update('person_type', 'PJ')"
        />
        {{ t('SALES.COMMISSION.PERSON_TYPES.COMPANY') }}
      </label>
    </div>
    <div class="grid gap-4 md:grid-cols-2">
      <LaisField :label="documentLabel" :error="documentError" required>
        <input
          :value="draft[documentField]"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @input="update(documentField, $event.target.value)"
        />
      </LaisField>
      <LaisField :label="t('SALES.COMMISSION.FIELDS.CRECI')">
        <input
          :value="draft.creci"
          :placeholder="t('SALES.COMMISSION.PLACEHOLDERS.CRECI')"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-3 text-sm"
          @input="update('creci', $event.target.value)"
        />
      </LaisField>
    </div>
    <SalesCommissionParticipantPayment
      :participant="draft"
      :banks="banks"
      :pix-keys="pixKeys"
      :errors="{ payment: pixError || errors.payment }"
      @update="update"
    />
    <template #footer>
      <div class="flex justify-end">
        <LaisPrimaryButton
          :disabled="!valid"
          icon="i-lucide-check"
          @click="save"
        >
          {{ t('SALES.COMMISSION.ACTIONS.SAVE_PAYMENT') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
